import { createRef, FC, Fragment, useEffect } from 'react'

import { Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import rafSchd from 'raf-schd'

import { BackgroundProvider } from '../BackgroundContext/index.js'
import { useDocument } from '../hooks/useDocument.js'
import {
  nodeContainsDescendant,
  wasContentNodeDescendant,
} from '../utils/descendant.js'
import { useFloatingFullContext } from '../utils/floating/index.js'
import { findFirstKeyboardFocusableNode } from '../utils/focus.js'
import { useEventListener } from '../utils/useEventListener.js'
import { useKeypressListener } from '../utils/useKeypressListener.js'
import { useOnClickOutside } from '../utils/useOnClickOutside.js'
import { PopoverAutofocusTarget, PopoverCloseSource } from './types.js'

export type PopoverPanelProps = {
  children?: React.ReactNode
  open: boolean
  onClose: (source: PopoverCloseSource) => void
  id: string
  className?: string
  activator: HTMLElement
  autofocusTarget?: PopoverAutofocusTarget
  fluidContent?: boolean
  rounded?: boolean
  allowOverflow?: boolean
}
export const PopoverPanel: FC<PopoverPanelProps> = props => {
  const { document } = useDocument()
  const {
    children,
    id,
    className,
    activator,
    autofocusTarget = 'container',
    open,
    onClose,
    fluidContent = true,
    rounded,
    allowOverflow,
    ...rest
  } = props

  const { x, y, strategy, floating, update } = useFloatingFullContext()
  const contentNode = createRef<HTMLDivElement>()

  useEffect(() => {
    const schedule = rafSchd(update)
    if (open) {
      schedule()
    }
    return () => schedule?.cancel()
  }, [update, open, children])

  const style = {
    position: strategy,
    top: '0',
    left: '0',
    transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
  }

  const focusContent = () => {
    if (autofocusTarget === 'none' || contentNode == null) {
      return
    }

    requestAnimationFrame(() => {
      if (contentNode.current == null) {
        return
      }

      const focusableChild = findFirstKeyboardFocusableNode(
        document,
        contentNode.current,
      )

      if (focusableChild && autofocusTarget === 'first-node') {
        focusableChild.focus()
      } else {
        contentNode.current.focus()
      }
    })
  }

  const FocusTrigger = () => {
    useEffect(() => {
      focusContent()
    }, [])
    return <div />
  }

  const handleClick = (event: Event) => {
    const target = event.target as HTMLElement

    if (!open) {
      return
    }

    const isDescendant =
      contentNode.current != null &&
      nodeContainsDescendant(contentNode.current, target)
    const isActivatorDescendant = nodeContainsDescendant(activator, target)

    if (isDescendant || isActivatorDescendant) {
      return
    }
    onClose(PopoverCloseSource.Click)
  }

  const handleEscape = (event: Event) => {
    if (!open) {
      return
    }

    const target = event.target as HTMLElement
    const composedPath = event.composedPath()
    const wasDescendant = wasContentNodeDescendant(composedPath, contentNode)
    const isActivatorDescendant = nodeContainsDescendant(activator, target)

    if (wasDescendant || isActivatorDescendant) {
      onClose(PopoverCloseSource.EscapeKeypress)
    }
  }

  const handleFocusFirstItem = () => {
    onClose(PopoverCloseSource.FocusOut)
  }

  const handleFocusLastItem = () => {
    onClose(PopoverCloseSource.FocusOut)
  }

  useOnClickOutside(contentNode, handleClick)
  useEventListener('click', handleClick)

  useKeypressListener('Escape', handleEscape)

  const content = (
    <div
      id={id}
      ref={contentNode}
      tabIndex={autofocusTarget === 'none' ? undefined : -1}
      className={clsx([
        'relative',
        'flex flex-col',
        rounded && 'rounded-full',
        !fluidContent ? 'max-h-none max-w-none' : 'md:max-w-sm max-h-120',
        className,
      ])}
    >
      {open && <FocusTrigger />}
      {children}
    </div>
  )

  if (!open) {
    return null
  }

  return (
    <div
      ref={floating}
      style={style}
      className={clsx(x === null && 'invisible')}
    >
      <BackgroundProvider backgroundType="surface">
        <Transition
          show={open}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <div
            className={clsx([
              !allowOverflow && 'overflow-hidden',
              'relative',
              'max-w-[calc(100vw-2rem)]',
              !fluidContent && 'w-[calc(100vw-2rem)] md:max-w-sm',
              'shadow-lg',
              'border border-line-subdued',
              'bg-surface text-content-subdued',
              rounded ? 'rounded-full' : 'rounded-md',
            ])}
            {...rest}
          >
            <div
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              onFocus={handleFocusFirstItem}
            />
            {content}
            <div
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              onFocus={handleFocusLastItem}
            />
          </div>
        </Transition>
      </BackgroundProvider>
    </div>
  )
}
